import { addDays, diasEntre } from './calendar';
import { fechaCaracas, fechaObs, hoyCaracas } from './freshness';
import { cambioPctExacto, productoExacto } from './numbers';
import { filtrarPrecios, valorMetrica } from './series';
import { calcularVariacion, type PuntoPrecio } from './stats';
import type {
  Alerta,
  AlertaFiltro,
  Historico,
  ISODate,
  Precio,
  PuntoHistorico,
  TasaBcv,
} from './types';

export { describirAlerta, detalleEvaluacion, notificacionDeAlerta } from './alertasTexto';

/**
 * Evaluación de alertas: función PURA, idéntica a `evaluar_alerta` del backend (BE-ALERTS). Los vectores
 * compartidos `contract-fixtures/alert-vectors.json` fijan esta semántica; si cambia algo aquí, cambian
 * los vectores y el servidor.
 *
 * ── Qué se mide ──
 * · objetivo `precio`: solo cuentan las filas en USD (única moneda del contrato) que cumplen el filtro.
 *   `rubro` y `categoria` deben coincidir; `presentacion: null` significa "filas SIN presentación" (igual
 *   que `SerieKey`); `region: null` y `fuente: null` significan "cualquiera".
 *   La fila elegida es la de fecha de observación (`fecha_fin ?? fecha`) más reciente. Valor = `metrica`
 *   (prom|min|max); con moneda `VES` se multiplica por la tasa BCV actual y se redondea a 2 decimales
 *   (mitad hacia arriba, aritmética decimal exacta).
 * · objetivo `bcv`: valor = tasa BCV actual (Bs por USD); `metrica` y `moneda` no intervienen.
 *
 * ── Desempate cuando varias filas cumplen el filtro ──
 * Varias fuentes o regiones pueden tener la MISMA fecha de observación más reciente (p. ej. ASODEGAA y
 * SORGO el 16/10). Se elige la fila más FAVORABLE a la condición: la de mayor valor para `sobre`, la de
 * menor valor para `bajo`, la de mayor Δ% para `sube_pct` y la de menor Δ% (más negativo) para `baja_pct`;
 * si aún empatan, gana la fuente primero en orden alfabético y luego la región (null antes que texto).
 * Justificación: el usuario que pide "avísame si el toro pasa de $2,80" quiere enterarse si ALGUNA de las
 * cotizaciones más frescas lo cumple; así agregar una fuente nueva nunca silencia un aviso, y el criterio
 * es determinista (no depende del orden en que lleguen las filas). Una fila más vieja nunca le gana a una
 * más reciente, aunque sea más favorable.
 *
 * ── Condiciones ──
 * `sobre`: valor ≥ umbral · `bajo`: valor ≤ umbral · `sube_pct`: Δ% ≥ umbral · `baja_pct`: Δ% ≤ −umbral.
 * Δ% = (actual − previo) / previo × 100 redondeado a 2 decimales (mitad hacia arriba, exacto) y se compara
 * ya redondeado, igual que el `dif_pct` que se muestra en pantalla. El `previo` es el de
 * `/v1/mercado/variaciones` para la misma serie (rubro, categoría, presentación, región y fuente de la fila
 * elegida): la observación más reciente con fecha ≤ fecha actual − `periodo_dias`; si no existe pero la
 * serie abarca ≥ periodo/2 días se usa la más antigua; si no, `sin_previo` (no dispara). Las observaciones
 * con promedio ≤ 0 se ignoran. Con `bcv` el previo es la tasa más reciente con fecha ≤ fecha de la tasa
 * actual − `periodo_dias` (sin la regla de "más antigua": igual que `variacion_bcv_pct`).
 *
 * ── Cooldown (solo si la condición se cumple) ──
 * `una_vez`: no vuelve a disparar si ya tiene `ultimaDisparada` (el servidor la desactiva al disparar).
 * `cada_dato`: una vez por fecha de observación nueva; bloquea si fechaDato ≤ `ultimaFechaDato`.
 * `diaria`: máximo una vez por día calendario de Caracas (UTC−4): 23:30 y 00:30 hora local son días distintos.
 * Una alerta inactiva (`activa: false` o `estado: 'pausada'`) nunca evalúa.
 */
export interface ContextoAlerta {
  precios: readonly Precio[];
  /** Tasa BCV vigente (Bs por USD). Necesaria para moneda VES y para objetivo bcv. */
  tasaBcvActual: number | null;
  /** Fecha valor de la tasa actual; por defecto, el día de Caracas de `ahora`. */
  tasaBcvFecha?: ISODate | null;
  /** Series históricas (para sube_pct/baja_pct de precios). */
  historicos?: readonly Historico[];
  /** Tasas BCV pasadas (para sube_pct/baja_pct del dólar). */
  bcvHistorico?: readonly TasaBcv[];
  /** Instante de la evaluación (Date, ISO con zona o milisegundos). */
  ahora: Date | string | number;
  /** `fecha_obs` del dato con el que se disparó la última vez (cooldown `cada_dato`). */
  ultimaFechaDato?: ISODate | null;
}

export type MotivoEvaluacion =
  'dispara' | 'no_cumple' | 'cooldown' | 'inactiva' | 'sin_datos' | 'sin_previo';

export interface ResultadoEvaluacion {
  motivo: MotivoEvaluacion;
  /** La condición se cumple (aunque el cooldown impida avisar). */
  cumple: boolean;
  /** Hay que avisar ahora: la condición se cumple y el cooldown lo permite. */
  dispara: boolean;
  /** `una_vez` que dispara: la alerta debe quedar desactivada. */
  desactivar: boolean;
  /** Valor actual en la moneda de la alerta (precio, o tasa BCV). */
  valor: number | null;
  valorPrevio: number | null;
  /** Δ% (solo sube_pct/baja_pct). */
  difPct: number | null;
  /** Fecha de observación del dato usado (para BCV, fecha de la tasa). */
  fechaDato: ISODate | null;
  /** Fila elegida (objetivo precio). */
  fila: Precio | null;
}

interface Medicion {
  valor: number | null;
  valorPrevio: number | null;
  difPct: number | null;
  fechaDato: ISODate;
  fila: Precio | null;
}

const esPct = (a: Pick<Alerta, 'condicion'>): boolean =>
  a.condicion === 'sube_pct' || a.condicion === 'baja_pct';

const tasaValida = (t: number | null | undefined): t is number =>
  typeof t === 'number' && Number.isFinite(t) && t > 0;

/* ───────────── objetivo: precio ───────────── */

const esUsd = (p: Precio): boolean => p.moneda.toUpperCase() === 'USD';

function candidatas(precios: readonly Precio[], f: AlertaFiltro): Precio[] {
  const filas = filtrarPrecios(precios, {
    rubro: f.rubro,
    categoria: f.categoria,
    presentacion: f.presentacion,
    region: f.region ?? undefined,
    fuente: f.fuente ?? undefined,
  });
  const usd = filas.filter(esUsd);
  const maxObs = usd.map(fechaObs).sort().at(-1);
  return usd.filter((p) => fechaObs(p) === maxObs);
}

/** Valor del precio en la moneda de la alerta; null si falta la tasa para convertir a bolívares. */
function enMoneda(alerta: Alerta, usd: number, tasa: number | null): number | null {
  if (alerta.moneda === 'USD') return usd;
  return tasaValida(tasa) ? productoExacto([usd, tasa], 2) : null;
}

const mismaSerie = (h: Historico, p: Precio): boolean =>
  h.rubro === p.rubro && h.categoria === p.categoria && h.presentacion === p.presentacion;

function puntosPrevios(historicos: readonly Historico[], fila: Precio, a: Alerta): PuntoPrecio[] {
  const obsFila = fechaObs(fila);
  const propios = (p: PuntoHistorico): boolean =>
    p.fuente === fila.fuente && (p.region ?? null) === (fila.region ?? null);
  const previos = historicos
    .filter((h) => mismaSerie(h, fila))
    .flatMap((h) => h.puntos.filter(propios))
    .filter((p) => fechaObs(p) < obsFila)
    .map((p) => ({ fecha: p.fecha, fechaFin: p.fechaFin, prom: valorMetrica(p, a.metrica) }));
  return [
    ...previos,
    { fecha: fila.fecha, fechaFin: fila.fechaFin, prom: valorMetrica(fila, a.metrica) },
  ];
}

function medirFila(a: Alerta, fila: Precio, ctx: ContextoAlerta): Medicion {
  const base = { fechaDato: fechaObs(fila), fila };
  const actual = valorMetrica(fila, a.metrica);
  const valor = enMoneda(a, actual, ctx.tasaBcvActual);
  if (!esPct(a)) return { ...base, valor, valorPrevio: null, difPct: null };
  const calculo = calcularVariacion(puntosPrevios(ctx.historicos ?? [], fila, a), a.periodoDias);
  const previo = calculo?.previo ?? null;
  return {
    ...base,
    valor,
    valorPrevio: previo === null ? null : enMoneda(a, previo, ctx.tasaBcvActual),
    difPct: previo === null ? null : cambioPctExacto(actual, previo, 2),
  };
}

/** Clave numérica "más alto = más favorable" de una medición para la condición de la alerta. */
function favorable(a: Alerta, m: Medicion): number {
  const x = esPct(a) ? m.difPct : m.valor;
  if (x === null) return Number.NEGATIVE_INFINITY;
  return a.condicion === 'bajo' || a.condicion === 'baja_pct' ? -x : x;
}

const textoAsc = (a: string | null, b: string | null): number => (a ?? '').localeCompare(b ?? '');

function medirPrecio(a: Alerta, ctx: ContextoAlerta): Medicion | null {
  if (!a.filtro) return null;
  const mediciones = candidatas(ctx.precios, a.filtro)
    .map((fila) => medirFila(a, fila, ctx))
    .filter((m) => m.valor !== null);
  mediciones.sort(
    (x, y) =>
      favorable(a, y) - favorable(a, x) ||
      textoAsc(x.fila?.fuente ?? null, y.fila?.fuente ?? null) ||
      textoAsc(x.fila?.region ?? null, y.fila?.region ?? null),
  );
  return mediciones[0] ?? null;
}

/* ───────────── objetivo: bcv ───────────── */

function medirBcv(a: Alerta, ctx: ContextoAlerta): Medicion | null {
  const tasa = ctx.tasaBcvActual;
  if (!tasaValida(tasa)) return null;
  const fechaDato = ctx.tasaBcvFecha ?? hoyCaracas(ctx.ahora);
  const base = { fechaDato, fila: null, valor: tasa };
  if (!esPct(a)) return { ...base, valorPrevio: null, difPct: null };
  const limite = addDays(fechaDato, -a.periodoDias);
  const previa = [...(ctx.bcvHistorico ?? [])]
    .filter((t) => t.usd > 0 && t.fecha < fechaDato && limite !== null && t.fecha <= limite)
    .sort((x, y) => (x.fecha < y.fecha ? -1 : 1))
    .at(-1);
  return {
    ...base,
    valorPrevio: previa?.usd ?? null,
    difPct: previa ? cambioPctExacto(tasa, previa.usd, 2) : null,
  };
}

/* ───────────── condición y cooldown ───────────── */

function cumpleCondicion(a: Alerta, m: Medicion): boolean {
  switch (a.condicion) {
    case 'sobre':
      return m.valor !== null && m.valor >= a.umbral;
    case 'bajo':
      return m.valor !== null && m.valor <= a.umbral;
    case 'sube_pct':
      return m.difPct !== null && m.difPct >= a.umbral;
    case 'baja_pct':
      return m.difPct !== null && m.difPct <= -a.umbral;
  }
}

function enCooldown(a: Alerta, fechaDato: ISODate, ctx: ContextoAlerta): boolean {
  switch (a.cooldown) {
    case 'una_vez':
      return a.ultimaDisparada !== null;
    case 'cada_dato':
      return !!ctx.ultimaFechaDato && diasEntre(ctx.ultimaFechaDato, fechaDato) <= 0;
    case 'diaria':
      return (
        a.ultimaDisparada !== null && fechaCaracas(a.ultimaDisparada) === hoyCaracas(ctx.ahora)
      );
  }
}

const vacio: ResultadoEvaluacion = {
  motivo: 'sin_datos',
  cumple: false,
  dispara: false,
  desactivar: false,
  valor: null,
  valorPrevio: null,
  difPct: null,
  fechaDato: null,
  fila: null,
};

export function evaluarAlerta(alerta: Alerta, ctx: ContextoAlerta): ResultadoEvaluacion {
  if (!alerta.activa || alerta.estado === 'pausada') return { ...vacio, motivo: 'inactiva' };
  const m = alerta.objetivo === 'bcv' ? medirBcv(alerta, ctx) : medirPrecio(alerta, ctx);
  if (!m) return vacio;
  const datos = {
    valor: m.valor,
    valorPrevio: m.valorPrevio,
    difPct: m.difPct,
    fechaDato: m.fechaDato,
    fila: m.fila,
  };
  if (esPct(alerta) && m.difPct === null) return { ...vacio, ...datos, motivo: 'sin_previo' };
  const cumple = cumpleCondicion(alerta, m);
  const bloqueada = cumple && enCooldown(alerta, m.fechaDato, ctx);
  const dispara = cumple && !bloqueada;
  const motivo: MotivoEvaluacion = dispara ? 'dispara' : cumple ? 'cooldown' : 'no_cumple';
  return {
    ...vacio,
    ...datos,
    motivo,
    cumple,
    dispara,
    desactivar: dispara && alerta.cooldown === 'una_vez',
  };
}
